import { ButtonProps, CtaStyle } from "./../components/Button/types";
import { DividerProps } from "@/components/Divider/types";
import { FormFieldProps } from "@/components/Form/types";
import { LinkProps } from "@/components/Link/types";
import { TypographyProps } from "@/components/Typography/types";
import { RecoverPasswordDataProps } from "@/data/types/recover-password";
import { PlanProps } from "./plan";
import { QRCodeScanResponse } from "@/interface/api/getFindQr/types";
import React from "react";
import { ButtonShape, ButtonType, ButtonVariant } from "@npm_leadtech/qr-ui-components";
import type { ChipProps } from "@/components/Chip/types";
import { LanguageCode } from "./languages";

export type Sizes = "xs" | "s" | "m" | "l" | "xl" | "note";

/****
 *  TODO: Refactor definición RichtextItem:
 * content / table / list deberían ser un único valor tipado,
 * puesto una RichtextItem sólo puede tener una de las 3
 * https://leadtech.atlassian.net/browse/QR-2944
 ****/

export type RichtextItem = {
  tag?: string | undefined;
  content?: string;
  classname?: string;
  spacing?: number;
  size?: Sizes;
  list?: ListItem[];
  table?: TableItem;
  id?: string;
  subcontent?: RichtextItem;
};

export type ListItem = {
  tag?: string | undefined;
  item?: string;
  nestedList?: RichtextItem;
  content?: string;
  component?: string;
  label?: string;
  type?: ChipProps["type"];
  color?: ChipProps["color"];
  id?: string;
};

export type TableItem = {
  thead: { item: string }[];
  tbody: { tr: { item: string }[] }[];
};

export enum Language {
  EN = "en",
}

export interface LandingPageProps extends PageProps {
  params: PageProps["params"] & {
    qrSlugLanding: keyof typeof QRSlugLandingPageEnum;
  };
}

export interface PageProps {
  params: {
    lang: LanguageCode;
  };
  searchParams?: { [key: string]: string | string[] | undefined };
}

export interface MyQrCodesDetailsPageProps extends PageProps {
  params: PageProps["params"] & {
    qrId: string;
  };
}

export interface MyQrCodesReviewsPageProps {
  params: {
    qrId: string;
    lang: LanguageCode;
  };
}

export interface QREditorPageProps extends PageProps {
  params: PageProps["params"] & {
    qrSlug: string;
  };
}

export interface IQrViewPage extends PageProps {
  params: {
    lang: LanguageCode;
    qrId: string;
    data?: QRCodeScanResponse;
  };
}

export type ValidationRule = {
  rule: string;
  message: string;
};

export interface InputFormField extends React.InputHTMLAttributes<HTMLInputElement> {
  component: "Input";
  data_qa: string;
  errorMessage?: ValidationRule[];
  customTag?: string;
  label?: string;
}

export type InputLibFormField = Omit<InputFormField, "component"> & {
  component: "InputLib";
};

export type PasswordLibFormField = Omit<InputFormField, "component"> & {
  component: "PasswordLib";
};

export type TextAreaLibFormField = Omit<InputFormField, "component"> & {
  component: "TextAreaLib";
  resize: "none" | "both" | "horizontal" | "vertical";
  rows?: number;
};

export interface SelectFormField extends Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  "options"
> {
  component: "Select";
  data_qa: string;
  errorMessage?: ValidationRule[];
  options: { value: string; label: string }[];
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  defaultSelected?: string;
}

export interface ButtonFormField extends Omit<ButtonProps, "dataQA"> {
  component: "Button";
  data_qa?: string;
  rounded?: boolean;
}
export interface ButtonLibFormField_ extends Omit<ButtonShape, "dataQA"> {
  component: "ButtonLib";
  data_qa: string;
}

export interface ButtonLibFormField {
  component: "Link" | "ButtonLib";
  data_qa: string;
  id?: string;
  label?: string;
  type?: ButtonType;
  variant?: ButtonVariant;
  name?: string;
  href?: string;
  className?: string;
  dataTestId?: string;
}

export interface TypographyFormField extends TypographyProps {
  component: "Typography";
}

export interface LinkFormField extends LinkProps {
  component: "Link";
}

export interface DividerFormField extends DividerProps {
  component: "Divider";
}

export interface DivFormField extends React.HTMLAttributes<HTMLDivElement> {
  component: "div";
  className?: string;
  formFields: T_FormField[];
}

export type T_FormField =
  | InputFormField
  | InputLibFormField
  | PasswordLibFormField
  | TextAreaLibFormField
  | SelectFormField
  | ButtonFormField
  | ButtonLibFormField
  | TypographyFormField
  | LinkFormField
  | DividerFormField
  | DivFormField;

export interface AuthDataProps extends PageDataProps {
  formFields: FormFieldProps[];
  authModeSwitcher: AuthModeSwitcherDataProps;
  invalidAuthMessage: string;
  disclaimer?: string;
}

export interface AuthModeSwitcherDataProps {
  richtext: RichtextDataProps;
  button: ButtonLibFormField;
}

export interface AuthCarouselProps {
  img: {
    src: string;
    alt: string;
    width: number;
    height: number;
  };
  title: string;
  subtitle: string;
}

export interface LoginImageProps {
  src: string;
  alt: string;
  width: number;
  height: number;
  title: string;
}

export interface PageDataProps {
  page: string;
  title: string;
}

export interface RichtextDataProps {
  Tag: string;
  tag?: string;
  content?: string;
  spacing?: number;
  size?: "xs" | "s" | "l" | "xl";
  list?: { item: string }[];
  className?: string;
  classname?: string;
  table?: {
    thead: { item: string }[];
    tbody: { tr: { item: string }[] }[];
  };
}

export interface BannerDataProps {
  title: string;
  subtitle?: string;
  list: string[];
}

export interface SucessModal {
  button: {
    id: string;
    label: string;
    to?: string;
    data_qa?: string;
  };
  image: string;
  richtext: RichtextDataProps[];
}

export type SubscriptionPlan = {
  id: string;
  "data-qa": string;
  selected?: boolean;
  /** Marks the plan the resubscription flow should pre-select for cancelled users */
  resubscriptionSelected?: boolean;
  header?: string;
  customClass?: string;
  name: {
    richtext: RichtextItem;
    chip?: string;
    chipColor?: ChipProps["color"];
  };
  price: {
    richtext: RichtextItem | RichtextItem[];
  };
  priceWithDiscount?: string;
  benefits: {
    richtext: RichtextItem;
  };
  link: string;
  label: string;
  data?: PlanProps;
  currency?: string;
};

export type ExtendedBenefits = {
  icon: string;
  title: string;
  subtitle: string;
  component?: string;
  label?: string;
  type?: ChipProps["type"];
  color?: ChipProps["color"];
  id?: string;
};

export type FaqsPricing = {
  title: string;
  faqs: {
    title: string;
    richtext: RichtextItem[];
  }[];
};

export interface LayoutPageModalData {
  login: AuthDataProps;
  register: AuthDataProps;
  recoverPassword: RecoverPasswordDataProps;
}

export interface LayoutPageData {
  modalData: LayoutPageModalData;
}

export interface LayoutPageProps {
  data: any;
  children: any;
}

export interface Social {
  [key: string]: string;
}

export interface Image {
  url: string;
  alt?: string;
}

export interface Link {
  image: string;
  text: string;
  url: string;
}
export type modalButton = {
  id: string;
  label: string;
  dataQa?: string;
  ctaStyle?: CtaStyle;
  shape?: ButtonShape;
  type?: ButtonType;
  variant?: ButtonVariant;
};

export interface ResetPaswordDataProps {
  confirmModal: {
    image: string;
    title: string;
    subtitle: string;
    cancelButton: modalButton;
    confirmButton: modalButton;
  };
  successModal: {
    image: string;
    title: string;
    text: string;
    button: modalButton;
  };
}

export const QRSlugLandingPageEnum = {
  vcard: "vcard-qr-code",
  pdf: "pdf-qr-code",
};
